import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './SearchableSelect.css';

export interface SearchableSelectOption {
  _id: number | string;
  name: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: number | string | null;
  onChange: (value: number | string | null) => void;
  /** Shown when no option is selected. */
  placeholder?: string;
  disabled?: boolean;
  /** Show a small × to clear the selection to null. Defaults to true. */
  allowClear?: boolean;
  id?: string;
  loading?: boolean;
}

// Loose comparison so "1" matches 1.
const idEq = (a: number | string | null, b: number | string | null) =>
  a != null && b != null && String(a) === String(b);

/**
 * Normalize for case- and diacritics-insensitive substring matching
 * (Czech names contain combining marks once decomposed via NFD).
 */
const normalize = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/**
 * SearchableSelect — single-value combobox. A styled control showing the
 * selected option's name (or placeholder). Clicking opens a dropdown with an
 * auto-focused search input that filters options case/diacritics-insensitively.
 * Mirrors the MultiSelect / FormField select visual language and the
 * click-outside pattern used across the app.
 */
const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  allowClear = true,
  id,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = id ? `${id}-listbox` : 'searchable-select-listbox';

  const selectedOption = options.find((opt) => idEq(opt._id, value));
  const filteredOptions = options.filter((opt) =>
    normalize(opt.name).includes(normalize(search))
  );

  // Close dropdown when clicking outside (mirrors NotificationBell / MultiSelect).
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-focus the search input when opening.
  useEffect(() => {
    if (isOpen) {
      const handle = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(handle);
    }
  }, [isOpen]);

  // Reset highlight when the filtered list changes.
  useEffect(() => {
    setActiveIndex(-1);
  }, [search]);

  // Scroll the active option into view.
  useEffect(() => {
    if (activeIndex < 0 || !isOpen) return;
    document
      .getElementById(`${listboxId}-opt-${activeIndex}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen, listboxId]);

  const open = () => {
    if (disabled) return;
    setIsOpen(true);
  };

  const handleSelect = (optValue: number | string | null) => {
    onChange(optValue);
    setIsOpen(false);
    setSearch('');
    setActiveIndex(-1);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearch('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearch('');
      setActiveIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filteredOptions.length === 0) return;
      setActiveIndex((prev) => (prev + 1) % filteredOptions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filteredOptions.length === 0) return;
      setActiveIndex((prev) => (prev - 1 + filteredOptions.length) % filteredOptions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const idx = activeIndex >= 0 ? activeIndex : 0;
      if (idx < filteredOptions.length) {
        handleSelect(filteredOptions[idx]._id);
      }
    }
  };

  const showClear = allowClear && !disabled && value != null;

  return (
    <div
      ref={containerRef}
      className={`searchable-select-container${isOpen ? ' open' : ''}${disabled ? ' disabled' : ''}`}
    >
      <div
        className="searchable-select-control"
        onClick={open}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === ' ') {
            e.preventDefault();
            open();
          }
        }}
        id={id}
      >
        <span
          className={`searchable-select-value${selectedOption ? '' : ' placeholder'}`}
        >
          {loading
            ? t('common.loading')
            : selectedOption
              ? selectedOption.name
              : placeholder || t('common.select')}
        </span>
        {showClear && (
          <button
            type="button"
            className="searchable-select-clear"
            onClick={handleClear}
            aria-label={t('common.clear', 'Clear')}
            tabIndex={-1}
          >
            &times;
          </button>
        )}
        <span className="searchable-select-arrow" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 12 12">
            <path
              d="M2 4 L6 8 L10 4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </span>
      </div>

      {isOpen && (
        <div className="searchable-select-dropdown">
          <div className="searchable-select-search-wrapper">
            <input
              ref={inputRef}
              type="text"
              className="searchable-select-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('common.search')}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={
                activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined
              }
            />
          </div>
          <div className="searchable-select-list" role="listbox" id={listboxId}>
            {filteredOptions.length === 0 ? (
              <div className="searchable-select-no-options" role="status">
                {t('common.noResults')}
              </div>
            ) : (
              filteredOptions.map((opt, idx) => (
                <div
                  key={String(opt._id)}
                  id={`${listboxId}-opt-${idx}`}
                  className={`searchable-select-option${idx === activeIndex ? ' active' : ''}${idEq(opt._id, value) ? ' selected' : ''}`}
                  onClick={() => handleSelect(opt._id)}
                  role="option"
                  aria-selected={idEq(opt._id, value)}
                >
                  {opt.name}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
